import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import {
  ElderFormFieldDenseDirective,
  ElderLabelInputComponent,
  ElderQuantityInputControlComponent,
  ElderTripleStateCheckboxDirective,
  ElderValidationErrorDirective,
  ParseUtil,
  Unit,
  ViewProviders,
} from '@elderbyte/ngx-starter'
import { Property, PropertyType } from '../property'
import { PropertyUtil } from '../property-util'
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox'
import { MatOption } from '@angular/material/core'
import { MatSelect } from '@angular/material/select'
import { FormsModule } from '@angular/forms'
import { MatInput } from '@angular/material/input'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'

@Component({
  selector: 'app-property-input',
  templateUrl: './property-input.component.html',
  styleUrls: ['./property-input.component.scss'],
  viewProviders: [ViewProviders.ParentControlContainer],
  imports: [
    MatFormField,
    ElderFormFieldDenseDirective,
    MatLabel,
    MatInput,
    FormsModule,
    MatError,
    ElderValidationErrorDirective,
    ElderLabelInputComponent,
    ElderQuantityInputControlComponent,
    MatCheckbox,
    ElderTripleStateCheckboxDirective,
    MatSelect,
    MatOption,
  ],
})
export class PropertyInputComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private _value: string | null
  private _values: string[] | null
  private _numberValue: number | null
  private _quantityUnit: Unit | null
  private _quantityValue: number | null
  private _booleanValue: boolean | null

  private _allowedValues: string[]

  public PropertyType = PropertyType

  @Input()
  public name: string | null

  @Input()
  public label: string | null

  @Input()
  public required: boolean

  @Input()
  public singleSelection: boolean

  @Input()
  public tripleStateBoolean: boolean

  @Input()
  public property: Property

  @Input()
  public showLabel: boolean

  @Output()
  public valuesChange: EventEmitter<string[]> = new EventEmitter<string[]>()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {}

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    this._value = this.hasValue() ? this.getSingleValue() : null

    this._numberValue = this.hasValue() ? this.getSingleNumberValue() : null

    this._values = this.hasValue() ? this.getValueList() : null

    this._quantityUnit = this.property.unit
    this._quantityValue = this.hasValue() ? this.getSingleNumberValue() : null

    this._booleanValue = this.hasValue() ? this.getSingleBooleanValue() : null

    if (!this.label || this.label.length <= 0) {
      this.label = this.property?.description
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get value(): string | null {
    return this._value
  }

  public set value(value: string) {
    this._value = value
    this.setSingleValue(value)
  }

  public get numberValue(): number | null {
    return this._numberValue
  }

  public set numberValue(value: number) {
    this._numberValue = value
    this.setSingleValue(value ? String(value) : null)
  }

  public get values(): string[] | null {
    return this._values
  }

  @Input()
  public set values(values: string[]) {
    this._values = values
    this.valuesChange.next(values)
  }

  @Input()
  public set allowedValues(values: string[]) {
    this._allowedValues = values
  }

  public get quantityUnit(): Unit | null {
    return this._quantityUnit
  }

  public set quantityValue(value: number) {
    this._quantityValue = value
    this.setSingleValue(value ? String(value) : null)
  }

  public get quantityValue(): number | null {
    return this._quantityValue
  }

  public get booleanValue(): boolean | null {
    return this._booleanValue
  }

  public set booleanValue(value: boolean | null) {
    this._booleanValue = value
    this.setSingleValue(value != null ? String(value) : null)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public isSingleSelection(): boolean {
    return this.singleSelection && PropertyUtil.supportsSingleSelectionSetting(this.property?.type)
  }

  public getAllowedValues(): string[] {
    return this._allowedValues?.length > 0 ? this._allowedValues : this.property?.values
  }

  public readTripleStateBooleanValue(): string | null {
    return this.hasValue() ? this.getSingleValue() : null
  }

  public applyTripleStateBooleanValue($event: boolean) {
    this.booleanValue = $event
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private hasValue(): boolean {
    return !!this.values && this.values.length > 0
  }

  private getSingleValue(): string {
    return this.values![0]
  }

  private setSingleValue(value: string | null): void {
    this.values = value != null ? [value] : []
  }

  private getValueList(): string[] {
    return this.values!
  }

  private getSingleNumberValue(): number {
    return Number(this.getSingleValue())
  }

  private getSingleBooleanValue(): boolean {
    return !!ParseUtil.parseBoolOrUndefined(this.getSingleValue())
  }
}
